<template>
  <div
    class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50"
  >
    <!-- Back Button -->
    <div class="absolute top-4 left-4 sm:top-6 sm:left-6">
      <button
        type="button"
        @click="handleBackNavigation"
        :disabled="authStore.loading"
        class="flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeftIcon class="h-4 w-4 mr-2" aria-hidden="true" />
        {{ t("common.back") }}
      </button>
    </div>

    <!-- Header -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="flex justify-center">
        <div class="flex items-center space-x-3">
          <PhoneIcon class="h-10 w-10 text-blue-600" />
          <ArrowRightIcon v-if="isPhoneBound" class="h-6 w-6 text-gray-400" />
          <CheckCircleIcon
            v-if="isPhoneBound"
            class="h-10 w-10 text-green-600"
          />
        </div>
      </div>
      <h2
        class="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900"
      >
        {{ isPhoneBound ? t("phone.rebindTitle") : t("phone.title") }}
      </h2>
      <p class="mt-2 text-center text-sm/6 text-gray-600">
        {{ isPhoneBound ? t("phone.rebindSubtitle") : t("phone.subtitle") }}
      </p>
    </div>

    <!-- Main Form -->
    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
        <!-- Current Phone Banner -->
        <div
          v-if="isPhoneBound"
          class="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg"
        >
          <div class="flex items-center space-x-3">
            <PhoneIcon class="h-6 w-6 text-blue-600 flex-shrink-0" />
            <div>
              <h3 class="text-sm font-medium text-blue-800">
                {{ t("phone.currentPhone") }}
              </h3>
              <p class="mt-1 text-sm text-blue-700">
                {{ userInfo?.phone_number }}
              </p>
            </div>
          </div>
        </div>

        <!-- Phone Form -->
        <form
          @submit.prevent="onFinish(formState)"
          class="space-y-6"
          method="post"
          action="#"
        >
          <!-- 手机号输入框 -->
          <div>
            <label
              for="phone"
              class="block text-sm/6 font-medium text-gray-900"
            >
              {{ t("phone.phoneNumber") }}
            </label>
            <div class="mt-2">
              <input
                id="phone"
                v-model="formState.phone_number"
                name="phone"
                type="tel"
                autocomplete="tel"
                required
                :disabled="authStore.loading"
                pattern="^1[3-9]\d{9}$"
                class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                :placeholder="t('phone.phonePlaceholder')"
              />
            </div>
            <p class="mt-2 text-xs text-gray-500">
              {{ t("phone.phoneHint") }}
            </p>
          </div>

          <!-- 操作按钮 -->
          <div>
            <button
              type="submit"
              :disabled="authStore.loading"
              class="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <a-spin v-if="authStore.loading" class="mr-2" size="small" />
              {{
                authStore.loading
                  ? t("phone.processing")
                  : isPhoneBound
                    ? t("phone.rebind")
                    : t("phone.confirm")
              }}
            </button>
          </div>
        </form>

        <!-- Divider -->
        <div class="relative mt-10">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-200" />
          </div>
          <div class="relative flex justify-center text-sm/6 font-medium">
            <span class="bg-white px-6 text-gray-900">{{
              t("phone.whySetPhone")
            }}</span>
          </div>
        </div>

        <!-- Help Information -->
        <div class="mt-6">
          <div class="p-4 bg-gray-50 rounded-lg">
            <div class="text-xs text-gray-600 leading-relaxed space-y-4">
              <!-- 手机号作用说明 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("phone.reasons.notifications") }}
                </h4>
                <p>{{ t("phone.notificationDesc") }}</p>
              </div>

              <!-- 安全保护 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("phone.reasons.security") }}
                </h4>
                <p>{{ t("phone.securityDesc") }}</p>
              </div>

              <!-- 账户保护 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("phone.reasons.protection") }}
                </h4>
                <p>{{ t("phone.protectionDesc") }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Cancel Button -->
        <div class="mt-6">
          <button
            type="button"
            @click="handleCancel"
            :disabled="authStore.loading"
            class="flex w-full justify-center rounded-md bg-gray-200 px-3 py-1.5 text-sm/6 font-semibold text-gray-700 shadow-xs hover:bg-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ t("common.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  PhoneIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/auth";
import { notificationService } from "@/services/notificationService";
import { reportError } from "@/utils/sentry";
import { jwtService } from "@/services/jwt";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();

const formState = reactive({
  phone_number: "",
});

// 使用 authStore 的响应式用户状态
const userInfo = computed(() => authStore.user);
const isPhoneBound = computed(() => !!authStore.user?.phone_number);

// 初始化用户信息（如果 authStore 中没有数据）
async function initializeUserInfo() {
  if (!authStore.user && jwtService.getToken()) {
    try {
      await authStore.fetchUserFromToken();
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "PhoneView",
        action: "initializeUserInfo",
      });
    }
  }
}

// 处理表单提交
async function onFinish(values: any) {
  if (isPhoneBound.value) {
    const confirmed = await notificationService.confirm(
      "phone.rebindConfirmTitle",
      "phone.rebindConfirmDesc",
    );
    if (confirmed) {
      handleRebindConfirm();
    }
    return;
  }
  await updatePhone(values);
}

// 更新手机号
async function updatePhone(values: any) {
  try {
    await authStore.updatePhone(values.phone_number);
    notificationService.success("phone.setSuccess");
    handleRedirect();
  } catch (error: any) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "PhoneView",
      action: "updatePhoneNumber",
      phoneNumber: values.phone_number,
      responseStatus: error.response?.status,
      responseData: error.response?.data,
    });
    const errorMsg = error.response?.data?.message || t("phone.setFailed");
    notificationService.error("phone.setError", errorMsg);
  }
}

// 处理换绑确认
function handleRebindConfirm() {
  updatePhone(formState);
}

// 处理取消
function handleCancel() {
  // 智能导航：优先使用 history 返回，失败则导航到安全页面
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push("/authorize");
  }
}

// 处理重定向
function handleRedirect() {
  const redirect = route.query.redirect as string;
  if (redirect) {
    router.replace(redirect);
  } else {
    router.replace("/authorize");
  }
}

// 返回处理
function handleBackNavigation() {
  try {
    // 直接导航到授权页面，避免 router.back() 的问题
    router.push("/authorize");
  } catch (error) {
    console.error("Back navigation error:", error);
    reportError(error as Error, {
      component: "PhoneView",
      action: "backNavigation",
    });
    // 回退到授权页面
    router.push("/authorize");
  }
}

onMounted(() => {
  initializeUserInfo();
});
</script>
